import React, { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  selectedProvincesState,
  selectedPemdaState,
  selectedJenisTkdState,
  months,
  startMonthAtom,
  endMonthAtom,
} from "states/recoil";
import {
  Center,
  Spinner,
  Text,
  CardBody,
  CardHeader,
  Heading,
  IconButton,
  Flex,
  Box,
  Menu,
  MenuList,
  MenuButton,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import { HiChartPie, HiOutlineDownload, HiArrowsExpand } from "react-icons/hi";
import Chart from "react-apexcharts";
import { DownloadImage } from "components/functions/DownloadImage";
import { ExportToExcel } from "components/functions/ExportToExcel";
import { ToggleFullScreen } from "components/functions/ToggleFullscreen";
import { UseTogglePopUp } from "components/functions/UseTogglePopUp";
import { tahunAnggaranAtom } from "states/recoil";
import { sheetURLSelector } from "states/recoil";

function DonutChart({ data, titleChart, selectedData }) {
  //ref to downloadImage
  const chartContainerRef = useRef(null);

  //recoil-state
  const [selectedProvinces, setSelectedProvinces] = useRecoilState(
    selectedProvincesState
  );
  const [selectedPemda, setSelectedPemda] = useRecoilState(selectedPemdaState);
  const [selectedJenisTKD, setSelectedJenisTKD] = useRecoilState(
    selectedJenisTkdState
  );
  const monthArray = useRecoilValue(months);
  const [selectedStartMonth] = useRecoilState(startMonthAtom);
  const [selectedEndMonth] = useRecoilState(endMonthAtom);

  //local state
  const [isLoading, setIsLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const { isOpen, openPopup, closePopup } = UseTogglePopUp();

  //chart state
  const [pieTotal, setPieTotal] = useState([""]);
  const [pieReal, setPieReal] = useState([""]);
  const [donutData, setDonutData] = useState({ series: [], labels: [] });
  const [donutOptions, setDonutOptions] = useState({});

  //get the data
  useEffect(() => {
    setRawData(data);
  }, [data]);

  //Set Chart Data
  useEffect(() => {
    setIsLoading(true);

    if (
      !rawData.length ||
      !selectedProvinces.length ||
      !selectedPemda.length ||
      !selectedJenisTKD.length
    ) {
      setDonutData({
        labels: [],
        series: [],
        categories: [],
      });
      return;
    }

    const filteredData = rawData.filter(
      (row) =>
        selectedProvinces.includes(row["NM PROV"]) &&
        selectedPemda.includes(row["NM PEMDA"]) &&
        selectedJenisTKD.includes(row["JENIS TKD"])
    );

    function calculateFilteredRealisasi(row, startMonth, endMonth) {
      let total = 0;
      const startIndex = monthArray.indexOf(startMonth);
      const endIndex = monthArray.indexOf(endMonth);

      for (let i = startIndex; i <= endIndex; i++) {
        const monthNumber = (i + 1).toString().padStart(2, "0");
        const key = `REALISASI ${monthNumber}`;
        total += parseFloat(row[key] || 0);
      }
      return total;
    }

    const grouped = {};
    filteredData.forEach((row) => {
      const jenis = row[selectedData];
      if (!jenis) return;

      if (!grouped[jenis]) {
        grouped[jenis] = {
          "PAGU ALOKASI": 0,
          REALISASI: 0,
        };
      }

      grouped[jenis]["PAGU ALOKASI"] += parseFloat(row["PAGU ALOKASI"] || 0);
      grouped[jenis]["REALISASI"] += calculateFilteredRealisasi(
        row,
        selectedStartMonth,
        selectedEndMonth
      );
    });
    const categories = Object.keys(grouped);

    const fullPagu = [];
    const fullRealisasi = [];

    categories.forEach((key) => {
      if (!grouped[key]) return;
      const pagu = grouped[key]["PAGU ALOKASI"];
      const real = grouped[key]["REALISASI"];
      fullPagu.push(pagu);
      fullRealisasi.push(real);
    });

    const sumPagu = fullPagu.reduce(
      (acc, val) => acc + (parseFloat(val) || 0),
      0
    );
    const sumRealisasi = fullRealisasi.reduce(
      (acc, val) => acc + (parseFloat(val) || 0),
      0
    );

    setPieTotal(sumPagu);
    setPieReal(sumRealisasi);

    const remaining = sumPagu - sumRealisasi;

    setDonutData({
      series: [
        { name: "Realisasi", data: [sumRealisasi] },
        { name: "Sisa Pagu", data: remaining > 0 ? [remaining] : [0] },
      ],
      labels: ["Realisasi", "Sisa Pagu"],
      categories: ["Nilai"],
    });
    setIsLoading(false);
  }, [
    rawData,
    selectedProvinces,
    selectedPemda,
    selectedJenisTKD,
    selectedStartMonth,
    selectedEndMonth,
  ]);

  //set chart options
  useEffect(() => {
    const totalFormatter = () => {
      const series = donutData.series || [];
      const total = (series[0] || 0) + (series[1] || 0);
      const realisasi = series[0] || 0;
      const percent = total > 0 ? (realisasi / total) * 100 : 0;
      return percent.toFixed(2) + "%";
    };

    setDonutOptions({
      labels: ["Realisasi", "Sisa Pagu"],
      colors: ["#00E396", "#005599"],
      legend: { position: "bottom" },
      dataLabels: { enabled: false },
      tooltip: {
        y: {
          formatter: (val) =>
            val.toLocaleString("id-ID", {
              style: "currency",
              currency: "IDR",
              maximumFractionDigits: 0,
            }),
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: { show: false },
              value: { show: false },
              total: {
                show: false,
                label: "Realisasi",
                fontSize: "14px",
                colors: "#000",
                fontWeight: "bold",
                formatter: totalFormatter,
              },
            },
          },
        },
      },
    });
  }, [donutData, selectedProvinces]);

  return (
    <>
      <CardHeader
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex align="center" gap={4}>
          <IconButton icon={<HiChartPie />} aria-label="chart" size="sm" />
          <Heading size="sm">{titleChart}</Heading>
        </Flex>
        <Flex gap={4}>
          <IconButton
            icon={<HiArrowsExpand />}
            aria-label="export"
            size="sm"
            onClick={openPopup}
          />
          <Menu>
            <MenuButton as={Button} size="sm">
              <HiOutlineDownload />
            </MenuButton>
            <MenuList
              maxHeight="300px"
              overflowY="auto"
              minWidth="250px"
              zIndex={3}
            >
              <MenuItem
                onClick={() =>
                  DownloadImage(chartContainerRef.current, titleChart)
                }
                title={titleChart}
              >
                Unduh sebagai gambar (.PNG)
              </MenuItem>
              <MenuItem
                onClick={() =>
                  ExportToExcel(donutData, `${titleChart}.xlsx`, false)
                }
              >
                Unduh sebagai spreadsheet (.xlsx)
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </CardHeader>
      <CardBody ref={chartContainerRef}>
        {isLoading ? (
          <Center w="100%" h="500px">
            <Spinner size="xl" color="blue.500" />
          </Center>
        ) : donutData.length === 0 ? (
          <Center w="100%" h="500px">
            <Text>Tidak ada data yang ditampilkan.</Text>
          </Center>
        ) : (
          <Box position="relative" w="100%" h="400px">
            {/* Overlay center box */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -180%)"
              zIndex="1"
              textAlign="center"
            >
              <Text fontSize="2xl" fontWeight="bold">
                {(() => {
                  const [realisasi, sisa] = donutData.series || [0, 0];
                  const total = realisasi + sisa;
                  const percent = pieTotal > 0 ? (pieReal / pieTotal) * 100 : 0;
                  return `${percent.toFixed(2)}%`;
                })()}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Realisasi
              </Text>
            </Box>

            {/* The donut chart */}
            <Chart
              options={donutOptions}
              series={[
                donutData.series[0].data[0] ? donutData.series[0].data[0] : 0,
                donutData.series[1].data[0] ? donutData.series[1].data[0] : 0,
              ]}
              type="donut"
              height={400}
              width="100%"
            />
          </Box>
        )}
      </CardBody>
      {isOpen && (
        <ToggleFullScreen onClose={closePopup} rawData={rawData}>
          {/* Render the expanded chart here */}
          <div
            style={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {isLoading ? (
              <Center w="100%" h="500px">
                <Spinner size="xl" color="blue.500" />
              </Center>
            ) : donutData.length === 0 ? (
              <Center w="100%" h="500px">
                <Text>Tidak ada data yang ditampilkan.</Text>
              </Center>
            ) : (
              <Box position="relative" w="100%" h="400px">
                {/* Overlay center box */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -90%)"
                  zIndex="1"
                  textAlign="center"
                >
                  <Text fontSize="4xl" fontWeight="bold">
                    {(() => {
                      const [realisasi, sisa] = donutData.series || [0, 0];
                      const total = realisasi + sisa;
                      const percent =
                        pieTotal > 0 ? (pieReal / pieTotal) * 100 : 0;
                      return `${percent.toFixed(2)}%`;
                    })()}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Realisasi
                  </Text>
                </Box>

                {/* The donut chart */}
                <Chart
                  options={donutOptions}
                  series={[
                    donutData.series[0].data[0]
                      ? donutData.series[0].data[0]
                      : 0,
                    donutData.series[1].data[0]
                      ? donutData.series[1].data[0]
                      : 0,
                  ]}
                  type="donut"
                  height={400}
                  width="100%"
                />
              </Box>
            )}
          </div>
        </ToggleFullScreen>
      )}
    </>
  );
}

export default DonutChart;
